import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaUniversity, FaGraduationCap, FaCertificate } from 'react-icons/fa';
import { 
  FaPython, FaJava, FaReact, FaAws, FaGithub, FaDatabase, 
  FaJs, FaCode, FaMicrochip, FaServer, FaCogs
} from 'react-icons/fa';
import { 
  SiCplusplus, SiFlask, SiElectron, SiDotnet, 
  SiPytorch, SiOpencv, SiScikitlearn, SiMongodb, 
  SiJsonwebtokens, SiSupabase
} from 'react-icons/si';
import { TbBrandCSharp } from 'react-icons/tb';
import { BiBrain } from 'react-icons/bi';
import { device } from '../../styles/responsive';

// Helper function to convert hex to rgb
const hexToRgb = (hex) => {
  // Remove the hash if it exists
  hex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Return as a string
  return `${r}, ${g}, ${b}`;
};

const SkillsSection = styled(motion.section)`
  height: 100vh;
  width: 100%;
  background-color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 0 1rem 3rem 1rem;
  margin: -6vh 0 0 0;
  
  @media ${device.md} {
    height: auto;
    min-height: 100vh;
    padding: 60px 1rem 80px 1rem;
    margin: 30px 0;
  }
  
  @media ${device.sm} {
    padding: 40px 1rem 70px 1rem;
    margin: 60px 0;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1600px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  position: relative;
  padding: 1rem;
  
  @media ${device.lg} {
    gap: 1.5rem;
  }
  
  @media ${device.md} {
    grid-template-columns: 1fr;
    gap: 3rem;
    padding: 0.5rem;
  }
  
  @media ${device.sm} {
    padding: 0.5rem;
    gap: 2.5rem;
  }
`;

const CategorySection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  max-width: 100%;
  
  @media ${device.md} {
    gap: 1rem;
  }
`;

const CategoryHeader = styled.div`
  position: relative;
  padding-bottom: 0.6rem;
  margin-bottom: 0.8rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 2px;
    background: #00ffff;
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.2);
    
    @media ${device.md} {
      width: 70px;
      height: 3px;
    }
  }

  h3 {
    color: #00ffff;
    font-size: 1.25rem;
    font-weight: 500;
    letter-spacing: 1.2px;
    margin: 0;
    text-transform: uppercase;
    text-shadow: 0 0 10px rgba(0, 255, 255, 0.2);
    
    @media ${device.md} {
      font-size: 1.4rem;
    }
    
    @media ${device.sm} {
      font-size: 1.3rem;
    }
  }

  p {
    color: #ffffff90;
    font-size: 0.8rem;
    margin: 0.4rem 0 0 0;
    letter-spacing: 0.3px;
    
    @media ${device.md} {
      font-size: 0.85rem;
      margin-top: 0.5rem;
    }
    
    @media ${device.sm} {
      font-size: 0.8rem;
    }
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
  align-items: start;
  max-width: 100%;
  
  @media ${device.md} {
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media ${device.sm} {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
  }
`;

const SkillCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(0, 255, 255, 0.03), transparent);
  border: 1px solid rgba(0, 255, 255, 0.1);
  padding: 0.7rem;
  border-radius: 8px;
  position: relative;
  backdrop-filter: blur(10px);
  transition: all 0.4s ease;
  display: flex;
  height: fit-content;
  max-width: 100%;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: #00ffff;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 255, 255, 0.05), transparent);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 8px;
  }

  &:hover {
    border-color: rgba(0, 255, 255, 0.15);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);

    &::before {
      transform: scaleX(${props => props.level * 0.2});
    }

    &::after {
      opacity: 1;
    }
  }
  
  @media ${device.md} {
    padding: 0.8rem;
    border-radius: 10px;
  }
  
  @media ${device.sm} {
    padding: 0.5rem;
    border-radius: 8px;
  }
`;

const SkillInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  width: 100%;
  z-index: 1;
`;

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  
  @media ${device.sm} {
    gap: 0.8rem;
  }
`;

const SkillIcon = styled.div`
  width: 28px;
  height: 28px;
  min-width: 28px;
  background: rgba(${props => hexToRgb(props.color || '#00ffff')}, 0.1);
  border: 1px solid rgba(${props => hexToRgb(props.color || '#00ffff')}, 0.2);
  color: ${props => props.color || '#00ffff'};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  @media ${device.md} {
    width: 26px;
    height: 26px;
    min-width: 26px;
  }
  
  @media ${device.sm} {
    width: 24px;
    height: 24px;
    min-width: 24px;
    border-radius: 5px;
  }
  
  svg {
    font-size: 1rem;
    
    @media ${device.sm} {
      font-size: 0.9rem;
    }
  }
  
  ${SkillCard}:hover & {
    background: rgba(${props => hexToRgb(props.color || '#00ffff')}, 0.15);
    border-color: rgba(${props => hexToRgb(props.color || '#00ffff')}, 0.3);
    transform: scale(1.1);
  }
`;

const SkillName = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media ${device.md} {
    font-size: 0.95rem;
  }
  
  @media ${device.sm} {
    font-size: 0.85rem;
  }
`;

const ExpertiseBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
  padding-left: 1.8rem;
  
  @media ${device.md} {
    display: none; /* Hide expertise badges on mobile */
  }
`;

const ExpertiseBadge = styled(motion.span)`
  font-size: 0.65rem;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.8);
  display: inline-block;
  letter-spacing: 0.3px;
  line-height: 1.3;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
  }
  
  @media ${device.sm} {
    font-size: 0.6rem;
    padding: 0.05rem 0.25rem;
  }
`;

const NavigationContainer = styled.div`
  position: absolute;
  bottom: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  padding: 0.8rem 1.2rem;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  z-index: 10;
  
  @media ${device.md} {
    bottom: 1.5rem;
    padding: 0.7rem 1rem;
  }
  
  @media ${device.sm} {
    bottom: 1rem;
    padding: 0.6rem 0.8rem;
  }
`;

const NavigationIndicator = styled.div`
  padding: 0 1.2rem;
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  
  @media ${device.md} {
    padding: 0 1rem;
    font-size: 0.95rem;
  }
  
  @media ${device.sm} {
    padding: 0 0.8rem;
    font-size: 0.8rem;
  }
`;

const NavText = styled.span`
  color: white;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media ${device.md} {
    font-size: 0.95rem;
    letter-spacing: 0.7px;
  }
  
  @media ${device.sm} {
    font-size: 0.8rem;
    letter-spacing: 0.5px;
  }
`;

const NavigationArrow = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.direction === 'left' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  
  @media ${device.md} {
    width: 30px;
    height: 30px;
  }
  
  @media ${device.sm} {
    width: 28px;
    height: 28px;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  
  svg {
    font-size: 0.9rem;
    color: white;
    
    @media ${device.sm} {
      font-size: 0.8rem;
    }
  }
`;

const TimelineContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media ${device.md} {
    padding: 0.5rem;
    justify-content: flex-start;
  }
`;

const ExperienceCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: 1400px;
  height: 100%;
  align-content: center;
  
  @media ${device.md} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  @media ${device.sm} {
    gap: 1rem;
  }
`;

const ExperienceCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.8rem;
  position: relative;
  overflow: hidden;
  height: 100%;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  
  @media ${device.md} {
    padding: 1.5rem;
    min-height: auto;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, transparent, #00ffff, transparent);
    transform-origin: left;
    opacity: 0.7;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 255, 255, 0.05), transparent);
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 12px;
    z-index: 0;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    border-color: rgba(0, 255, 255, 0.2);
    
    &::after {
      opacity: 1;
    }
  }
  
  transition: all 0.4s ease;
`;

const ExperienceDate = styled.div`
  font-size: 0.85rem;
  color: #00ffff;
  font-weight: 400;
  letter-spacing: 1px;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background: #00ffff;
    border-radius: 50%;
    box-shadow: 0 0 8px rgba(0, 255, 255, 0.5);
  }
`;

const ExperienceTitle = styled.h3`
  color: #ffffff;
  font-size: 1.4rem;
  margin: 0 0 0.3rem 0;
  font-weight: 500;
  z-index: 1;
`;

const ExperienceCompany = styled.h4`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
  font-weight: 400;
  position: relative;
  padding-bottom: 0.75rem;
  z-index: 1;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: #00ffff;
    box-shadow: 0 0 8px rgba(0, 255, 255, 0.4);
  }
`;

const ExperienceDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  flex-grow: 1;
  z-index: 1;
`;

const ExperienceIcon = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 1.75rem;
  color: rgba(0, 255, 255, 0.2);
  z-index: 0;
`;

const HighlightPoint = styled(motion.div)`
  background: rgba(0, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.5rem 0.8rem;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '→';
    color: #00ffff;
    font-weight: bold;
  }
`;

const ResponsibilitiesList = styled.ul`
  margin: 1rem 0;
  padding-left: 1.5rem;
  list-style-type: none;
`;

const ResponsibilityItem = styled(motion.li)`
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  position: relative;
  line-height: 1.5;
  
  &::before {
    content: '•';
    color: #00ffff;
    position: absolute;
    left: -1.2rem;
    font-size: 1.2rem;
  }
`;

const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 1rem;
  
  @media ${device.md} {
    display: none; /* Hide skill tags on mobile */
  }
`;

const SkillTag = styled(motion.span)`
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  background: rgba(0, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  display: inline-block;
  letter-spacing: 0.5px;
  border: 1px solid rgba(0, 255, 255, 0.2);
  white-space: nowrap;
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.2), transparent);
  margin: 1rem 0;
`;

const LocationBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.75rem;
  
  svg {
    color: rgba(0, 255, 255, 0.6);
    font-size: 0.9rem;
  }
`;

const EducationContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media ${device.md} {
    padding: 1rem 0.5rem;
    gap: 1.5rem;
  }
  
  @media ${device.sm} {
    gap: 1rem;
    padding: 0.5rem;
  }
`;

const AcademicCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  position: relative;
  width: 100%;
  max-width: 900px;
  
  @media ${device.md} {
    padding: 1.5rem;
    border-radius: 15px;
  }
  
  @media ${device.sm} {
    padding: 1.2rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, transparent, #00ffff, transparent);
    transform-origin: left;
    opacity: 0.7;
  }
  
  &:hover {
    border-color: rgba(0, 255, 255, 0.2);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
  
  transition: all 0.4s ease;
`;

const DegreeTitle = styled.h3`
  color: #ffffff;
  font-size: 1.6rem;
  margin: 0 0 0.8rem 0;
  font-weight: 500;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  @media ${device.md} {
    font-size: 1.4rem;
    gap: 0.6rem;
  }
  
  @media ${device.sm} {
    font-size: 1.2rem;
    gap: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
  
  svg {
    color: #00ffff;
    font-size: 1.4rem;
    
    @media ${device.sm} {
      font-size: 1.2rem;
      margin-bottom: 0.5rem;
    }
  }
`;

const UniversityName = styled.h4`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  margin: 0 0 1.2rem 0;
  font-weight: 400;
  position: relative;
  padding-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: #00ffff;
    box-shadow: 0 0 8px rgba(0, 255, 255, 0.4);
  }
  
  svg {
    color: #00ffff;
  }
`;

const StudyPeriod = styled.div`
  font-size: 0.9rem;
  color: #ffffff;
  font-weight: 400;
  letter-spacing: 1px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #00ffff;
    border-radius: 50%;
    box-shadow: 0 0 8px rgba(0, 255, 255, 0.5);
  }
`;

const EducationRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 0.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CoursesContainer = styled.div`
  flex: 1;
  min-width: 280px;
`;

const CoursesList = styled.div`
  background: rgba(0, 255, 255, 0.03);
  border: 1px solid rgba(0, 255, 255, 0.08);
  border-radius: 8px;
  padding: 1rem;
`;

const CoursesTitle = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #00ffff;
    font-size: 0.9rem;
  }
`;

const CoursesText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.6;
`;

const GPA = styled(motion.div)`
  background: rgba(0, 255, 255, 0.08);
  border: 1px solid rgba(0, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.8rem 1.2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  margin-top: 0.5rem;
  
  span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1rem;
  }
  
  strong {
    color: #00ffff;
    font-weight: 500;
    font-size: 1.1rem;
  }
  
  svg {
    color: #ffffff;
    font-size: 1.2rem;
  }
`;

const CertificationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.2rem;
  width: 100%;
  margin-top: 2.5rem;
  justify-content: center;
  
  @media ${device.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-top: 1.5rem;
  }
  
  @media ${device.sm} {
    grid-template-columns: 1fr;
    gap: 0.8rem;
    margin-top: 1rem;
  }
`;

const CertificationCard = styled(motion.div)`
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 255, 255, 0.15);
  border-radius: 10px;
  padding: 1.4rem;
  height: 100%;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media ${device.md} {
    padding: 1.2rem;
    min-height: 120px;
  }
  
  @media ${device.sm} {
    min-height: auto;
    padding: 1rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 0% 0%,
      rgba(0, 255, 255, 0.08),
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(0, 255, 255, 0.3);
    box-shadow: 
      0 10px 25px rgba(0, 0, 0, 0.3),
      0 0 15px rgba(0, 255, 255, 0.1);
    
    &::before {
      opacity: 1;
    }
  }
  
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
`;

const CertificationContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CertificationBadge = styled.div`
  position: absolute;
  top: 0;
  right: 1.5rem;
  background: linear-gradient(90deg, rgba(0, 255, 255, 0.2), rgba(0, 100, 255, 0.2));
  color: #00ffff;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 0.25rem 0.6rem;
  border-radius: 0 0 6px 6px;
  text-transform: uppercase;
  box-shadow: 0 2px 10px rgba(0, 255, 255, 0.1);
  backdrop-filter: blur(5px);
`;

const CertificationIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: rgba(0, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 255, 0.2);
  color: #00ffff;
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0, 255, 255, 0.1), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${CertificationCard}:hover &::before {
    opacity: 1;
  }
  
  svg {
    font-size: 1.3rem;
    filter: drop-shadow(0 0 5px rgba(0, 255, 255, 0.3));
  }
`;

const CertificationTitle = styled.h4`
  color: white;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.4;
  margin: 0 0 0.4rem 0;
  letter-spacing: 0.2px;
  position: relative;
  padding-right: 0.5rem;
  min-height: 2.8rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CertificationIssuer = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.85rem;
  position: relative;
  padding-top: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 2.5rem;
    height: 1px;
    background: linear-gradient(90deg, rgba(0, 255, 255, 0.3), transparent);
  }
  
  svg {
    color: #00ffff;
    font-size: 0.85rem;
    opacity: 0.9;
  }
`;

const experienceData = [
  {
    date: "June 2023 - Present",
    title: "Freelancer",
    company: "Online",
    location: "Remote",
    description: "Reaching out to potential clients looking for a software developer through social media and freelancing platforms like Reddit, Freelancer.com etc.",
    responsibilities: [
      "Identified and engaged with potential clients seeking software development services",
      "Developed custom solutions based on client requirements and technical specifications",
      "Managed project timelines and deliverables independently",
      "Maintained regular communication with clients throughout project development"
    ],
    skills: ["Client Acquisition", "Project Management", "Full-Stack Development", "Client Communication"],
    icon: <FaCode />,
    highlight: "Successfully delivered projects on time and within scope"
  },
  {
    date: "July 2021 - January 2022",
    title: "Sales Advisor",
    company: "Best Buy",
    location: "St. Johns, Newfoundland (Canada)",
    description: "Specialized in home entertainment systems, recommending products tailored to customer needs and lifestyle. Created personalized solutions and protection plans within clients' budgets.",
    responsibilities: [
      "Consulted with customers to understand their entertainment needs and space requirements",
      "Designed customized home theater solutions with appropriate components and accessories",
      "Explained complex technical features in accessible language to non-technical customers",
      "Coordinated with installation teams to ensure proper setup of purchased systems"
    ],
    skills: ["Audio/Visual Systems", "Solution Design", "Consultative Selling", "Customer Needs Analysis"],
    icon: <FaServer />,
    highlight: "Consistently exceeded monthly sales targets by 15%"
  },
  {
    date: "September 2020 - February 2021",
    title: "Sales Advisor / Tech Support",
    company: "Concentrix (Apple)",
    location: "Remote",
    description: "Provided expert consultation and technical support for Apple products. Helped customers with purchase decisions and resolved complex technical issues.",
    responsibilities: [
      "Assisted customers with product selection based on their specific needs and usage patterns",
      "Troubleshooted hardware and software issues across the Apple ecosystem",
      "Managed the complete sales cycle from product demonstration to purchase completion",
      "Maintained in-depth knowledge of all Apple products and service offerings"
    ],
    skills: ["Apple Ecosystem", "Technical Troubleshooting", "Customer Service", "Upselling", "Product Knowledge"],
    icon: <FaMicrochip />,
    highlight: "Generated $5000 in revenue from hero product sales"
  },
];

const ExperienceTimeline = () => {
  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: i => ({ 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        delay: i * 0.2
      }
    }),
    hover: {
      y: -8,
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.2)",
      transition: { duration: 0.3 }
    }
  };
  
  const highlightVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { delay: 0.4 } }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: i => ({ 
      opacity: 1, 
      x: 0, 
      transition: { delay: 0.3 + (i * 0.1) } 
    })
  };
  
  const tagVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: i => ({ 
      opacity: 1, 
      scale: 1, 
      transition: { delay: 0.6 + (i * 0.05) } 
    })
  };

  return (
    <TimelineContainer>
      <ExperienceCards>
        {experienceData.map((exp, index) => (
          <ExperienceCard
            key={index}
            variants={cardVariants}
            custom={index}
            initial="hidden"
            animate="visible"
            whileHover="hover"
          >
            <ExperienceIcon>{exp.icon}</ExperienceIcon>
            <ExperienceDate>{exp.date}</ExperienceDate>
            <ExperienceTitle>{exp.title}</ExperienceTitle>
            <ExperienceCompany>{exp.company}</ExperienceCompany>
            
            <LocationBox>
              <FaMapMarkerAlt /> {exp.location}
            </LocationBox>
            
            <ExperienceDescription>{exp.description}</ExperienceDescription>
            
            <SectionDivider />
            
            <ResponsibilitiesList>
              {exp.responsibilities.map((resp, i) => (
                <ResponsibilityItem
                  key={i}
                  variants={itemVariants}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                >
                  {resp}
                </ResponsibilityItem>
              ))}
            </ResponsibilitiesList>
            
            <SkillTags>
              {exp.skills.map((skill, i) => (
                <SkillTag
                  key={skill}
                  variants={tagVariants}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                >
                  {skill}
                </SkillTag>
              ))}
            </SkillTags>
            
            <HighlightPoint
              variants={highlightVariants}
              initial="hidden"
              animate="visible"
            >
              {exp.highlight}
            </HighlightPoint>
          </ExperienceCard>
        ))}
      </ExperienceCards>
    </TimelineContainer>
  );
};

const getIconForSkill = (skillName) => {
  try {
    switch(skillName.toLowerCase()) {
      case 'python': return <FaPython />;
      case 'c++': return <SiCplusplus />;
      case 'c#': return <TbBrandCSharp />;
      case 'java': return <FaJava />;
      case 'javascript': return <FaJs />;
      case 'react': return <FaReact />;
      case 'flask': return <SiFlask />;
      case 'react native': return <FaReact />;
      case 'electron': return <SiElectron />;
      case '.net': return <SiDotnet />;
      case 'pytorch': return <SiPytorch />;
      case 'hugging face': return <BiBrain />;
      case 'opencv': return <SiOpencv />;
      case 'scikit-learn': return <SiScikitlearn />;
      case 'weka': return <FaMicrochip />;
      case 'mongodb': return <SiMongodb />;
      case 'github': return <FaGithub />;
      case 'aws': return <FaAws />;
      case 'supabase': return <SiSupabase />;
      case 'sql': return <FaDatabase />;
      case 'json': return <SiJsonwebtokens />;
      default: return <FaCode />;
    }
  } catch (error) {
    console.error("Error rendering icon for", skillName, error);
    return <FaCode />;
  }
};

const academicEducation = {
  degree: "Bachelor of Engineering (Hons) Software Engineering",
  university: "University of Greater Manchester",
  period: "Jan 2023 - May 2025",
  courses: "Machine Learning, Big Data, Advanced Programming, Agile Programming",
  gpa: "4.0 (Expected First-Class Honors)"
};

const certifications = [
  {
    title: "AI Applications with Watson",
    issuer: "IBM"
  },
  {
    title: "Python Basics for Data Science",
    issuer: "IBM"
  },
  {
    title: "AI Chatbots without programming",
    issuer: "IBM"
  },
  {
    title: "Software Development Lifecycle",
    issuer: "University of Minnesota"
  },
  {
    title: "C Programming with Linux",
    issuer: "Dartmouth University"
  },
  {
    title: "Computer Science for Python Programming",
    issuer: "HarvardX"
  },
  {
    title: "Java Programming and Software Engineering Fundamentals",
    issuer: "Duke University"
  }
];

const EducationSection = () => {
  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 30,
      scale: 0.97
    },
    visible: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };
  
  const certCardVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: i => ({ 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        delay: 0.1 + (i * 0.1)
      }
    })
  };
  
  const gpaVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { delay: 0.3 } }
  };

  return (
    <EducationContainer>
      <AcademicCard
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <DegreeTitle>
          <FaGraduationCap />
          {academicEducation.degree}
        </DegreeTitle>
        
        <UniversityName>
          <FaUniversity /> {academicEducation.university}
        </UniversityName>
        
        <StudyPeriod>{academicEducation.period}</StudyPeriod>
        
        <CoursesList>
          <CoursesTitle>Relevant Coursework</CoursesTitle>
          <CoursesText>{academicEducation.courses}</CoursesText>
        </CoursesList>
        
        <GPA
          variants={gpaVariants}
          initial="hidden"
          animate="visible"
          style={{ marginTop: '1.5rem' }}
        >
          <FaGraduationCap />
          <span>GPA: <strong>{academicEducation.gpa}</strong></span>
        </GPA>
      </AcademicCard>
      
      <CertificationsGrid>
        {certifications.map((cert, index) => (
          <CertificationCard
            key={index}
            variants={certCardVariants}
            custom={index}
            initial="hidden"
            animate="visible"
            whileHover={{ scale: 1.02 }}
          >
            <CertificationBadge>Certified</CertificationBadge>
            <CertificationContent>
              <CertificationIconContainer>
                <FaCertificate />
              </CertificationIconContainer>
              <CertificationTitle>{cert.title}</CertificationTitle>
              <CertificationIssuer>
                <FaUniversity />
                {cert.issuer}
              </CertificationIssuer>
            </CertificationContent>
          </CertificationCard>
        ))}
      </CertificationsGrid>
    </EducationContainer>
  );
};

const Skills = () => {
  const [currentSection, setCurrentSection] = useState('skills');
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: false
  });
  // Track touch position for swipe
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNavigation('right');
    }
    if (isRightSwipe) {
      handleNavigation('left');
    }
  };

  const sections = [
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'experience', label: 'Experience' }
  ];
  
  const handleNavigation = (direction) => {
    const currentIndex = sections.findIndex(section => section.id === currentSection);
    if (direction === 'right') {
      const nextIndex = (currentIndex + 1) % sections.length;
      setCurrentSection(sections[nextIndex].id);
    } else {
      const prevIndex = (currentIndex - 1 + sections.length) % sections.length;
      setCurrentSection(sections[prevIndex].id);
    }
  };

  const getSectionTitle = () => {
    switch(currentSection) {
      case 'skills':
        return 'Technical Skills';
      case 'education':
        return 'Education';
      case 'experience':
        return 'Professional Journey';
      default:
        return 'Technical Skills';
    }
  };

  const containerVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.98
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.15
      }
    },
    exit: {
      opacity: 0,
      scale: 0.98,
      transition: {
        duration: 0.6,
        ease: "easeIn",
        staggerChildren: 0.1,
        staggerDirection: -1
      }
    }
  };

  const categoryVariants = {
    hidden: { 
      opacity: 0,
      y: 30
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    },
    exit: {
      opacity: 0,
      y: -30,
      transition: {
        duration: 0.5,
        ease: "easeIn"
      }
    }
  };

  const skillVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      y: -15,
      scale: 0.95,
      transition: {
        duration: 0.4,
        ease: "easeIn"
      }
    }
  };

  const categories = [
    {
      name: "Core Development",
      color: "#00ffff",
      description: "Foundation of software engineering",
      skills: [
        { 
          name: "Python",
          level: 5,
          details: "Primary language for development, data analysis, and ML applications.",
          expertise: ["ML Development", "Data Analysis", "API Development", "Automation"]
        },
        { 
          name: "C++",
          level: 4,
          details: "Used for high-performance systems and optimization.",
          expertise: ["Performance Critical", "System Programming", "Game Development"]
        },
        {
          name: "C#",
          level: 4,
          details: "Used for desktop applications and .NET development.",
          expertise: [".NET Apps", "Desktop Apps", "Game Development"]
        },
        {
          name: "Java",
          level: 3.5,
          details: "Enterprise application development and Android.",
          expertise: ["Android Apps", "Enterprise Software", "Multi-threading"]
        },
        {
          name: "JavaScript",
          level: 4.5,
          details: "Essential language for web development and interactive applications.",
          expertise: ["Web Development", "Frontend", "Node.js", "Browser APIs"]
        }
      ]
    },
    {
      name: "Web & Applications",
      color: "#00ffff",
      description: "Modern application development",
      skills: [
        {
          name: "React",
          level: 4.25,
          details: "Front-end development with modern React patterns and hooks.",
          expertise: ["SPA Development", "Component Design", "State Management", "UI/UX"]
        },
        {
          name: "Flask",
          level: 4.5,
          details: "Python web framework for building APIs and services.",
          expertise: ["REST APIs", "Microservices", "Database Integration", "Web Applications"]
        },
        {
          name: "React Native",
          level: 4,
          details: "Cross-platform mobile development.",
          expertise: ["iOS Apps", "Android Apps", "Cross-platform Mobile", "Native Device Features"]
        },
        {
          name: "Electron",
          level: 3.5,
          details: "Desktop applications with web technologies.",
          expertise: ["Cross-platform Desktop", "Native Integration", "App Distribution"]
        },
        {
          name: ".NET",
          level: 4,
          details: "Microsoft's framework for building Windows applications.",
          expertise: ["Windows Apps", "Enterprise Solutions", "Web Services"]
        }
      ]
    },
    {
      name: "AI & Machine Learning",
      color: "#00ffff",
      description: "Advanced AI capabilities",
      skills: [
        {
          name: "PyTorch",
          level: 4.5,
          details: "Deep learning model development and training.",
          expertise: ["Deep Learning", "Neural Networks", "Model Training", "GPU Acceleration"]
        },
        {
          name: "Hugging Face",
          level: 4,
          details: "NLP tasks and transformer models.",
          expertise: ["NLP", "Text Generation", "Sentiment Analysis", "Fine-tuning"]
        },
        {
          name: "OpenCV",
          level: 2.25,
          details: "Computer vision processing and applications.",
          expertise: ["Image Processing", "Object Detection", "Video Analysis", "Real-time Vision"]
        },
        {
          name: "Scikit-learn",
          level: 4,
          details: "ML algorithms for classification and regression.",
          expertise: ["Data Analysis", "Predictive Modeling", "Feature Engineering", "Model Evaluation"]
        },
        {
          name: "Weka",
          level: 2.5,
          details: "Collection of machine learning algorithms for data mining tasks.",
          expertise: ["Data Mining", "Classification", "Clustering", "Regression"]
        }
      ]
    },
    {
      name: "Infrastructure & Data",
      color: "#00ffff",
      description: "Development operations & data management",
      skills: [
        {
          name: "MongoDB",
          level: 4.5,
          details: "NoSQL database design and optimization.",
          expertise: ["Database Design", "Data Modeling", "Query Optimization", "Scalable Storage"]
        },
        {
          name: "GitHub",
          level: 4.25,
          details: "Version control and CI/CD pipelines.",
          expertise: ["Version Control", "CI/CD Pipelines", "Code Review", "Team Collaboration"]
        },
        {
          name: "AWS",
          level: 2.75,
          details: "Cloud infrastructure and serverless computing.",
          expertise: ["Cloud Deployment", "Serverless", "S3 Storage", "Lambda Functions"]
        },
        {
          name: "Supabase",
          level: 5.0,
          details: "Open source Firebase alternative with PostgreSQL backend.",
          expertise: ["Real-time Data", "Auth System", "PostgreSQL", "API Management"]
        },
        {
          name: "SQL",
          level: 4.5,
          details: "Relational database query language.",
          expertise: ["Data Querying", "Database Management", "Performance Tuning", "Data Analysis"]
        },
        {
          name: "JSON",
          level: 3.5,
          details: "Lightweight data interchange format.",
          expertise: ["API Integration", "Data Storage", "Configuration", "Web Development"]
        }
      ]
    }
  ];

  return (
    <SkillsSection
      ref={ref}
      id="skills"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      exit="exit"
      variants={containerVariants}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <AnimatePresence mode="wait">
        {currentSection === 'skills' && (
          <ContentContainer>
            {categories.map((category, categoryIndex) => (
              <CategorySection
                key={category.name}
                variants={categoryVariants}
                custom={categoryIndex}
              >
                <CategoryHeader color={category.color}>
                  <motion.h3
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: categoryIndex * 0.1 }}
                  >
                    {category.name}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: categoryIndex * 0.1 + 0.1 }}
                  >
                    {category.description}
                  </motion.p>
                </CategoryHeader>
                <SkillsGrid>
                  {category.skills.map((skill, skillIndex) => (
                    <SkillCard
                      key={skill.name}
                      color={category.color}
                      level={skill.level}
                      variants={skillVariants}
                      whileHover={{ y: -4, transition: { duration: 0.2 } }}
                      transition={{
                        duration: 0.3,
                        delay: categoryIndex * 0.1 + skillIndex * 0.05
                      }}
                    >
                      <SkillInfo>
                          <SkillHeader>
                            <SkillIcon color={category.color}>
                              {getIconForSkill(skill.name)}
                            </SkillIcon>
                        <SkillName>{skill.name}</SkillName>
                          </SkillHeader>
                        <ExpertiseBadges>
                          {skill.expertise && skill.expertise.map((exp, i) => (
                            <ExpertiseBadge
                              key={exp}
                              color={category.color}
                              initial={{ opacity: 0, y: 5 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -5 }}
                              transition={{ delay: i * 0.1 }}
                            >
                              {exp}
                            </ExpertiseBadge>
                          ))}
                        </ExpertiseBadges>
                      </SkillInfo>
                    </SkillCard>
                  ))}
                </SkillsGrid>
              </CategorySection>
            ))}
          </ContentContainer>
        )}
        
        {currentSection === 'experience' && (
          <ExperienceTimeline />
        )}
        
        {currentSection === 'education' && (
          <EducationSection />
        )}
      </AnimatePresence>
      
      <NavigationContainer>
        <NavigationArrow 
          direction="left" 
          onClick={() => handleNavigation('left')}
        >
          <FaChevronLeft />
        </NavigationArrow>
        
        <NavigationIndicator>
          <NavText>{getSectionTitle()}</NavText>
        </NavigationIndicator>

        <NavigationArrow 
          direction="right" 
          onClick={() => handleNavigation('right')}
        >
          <FaChevronRight />
        </NavigationArrow>
      </NavigationContainer>
    </SkillsSection>
  );
};

export default Skills; 